import { AppliedFilters, Table } from "@fluidattacks/design";
import { useCallback, useEffect, useState } from "react";

import { AllPackagesTableActions } from "./actions";
import { renderExpandedRow } from "./expanded-row";
import { useGetPackages } from "./hooks/use-get-packages";
import { usePackagesFilters } from "./hooks/use-packages-filters";
import { getTableColumns } from "./utils";

import type { IPackage } from "../types";
import type {
  GetPackagesQueryVariables as PackagesVars,
  ToePackagesInput,
  ToePackagesSort,
  ToePackagesSortInput,
} from "gql/graphql";
import { useTable } from "hooks/use-table";
import { useTableServerSideStore } from "utils/tables";
import type { TStore } from "utils/zustand";

interface IAllPackagesTabProps {
  groupName: string;
}

const AllPackagesTab = (props: IAllPackagesTabProps): React.JSX.Element => {
  const { groupName } = props;
  const [backFilters, setBackFilters] = useState<PackagesVars["filters"]>({});
  const tableHelpers = useTableServerSideStore();
  const columns = getTableColumns();

  const { packages, fetchMore, loading, total } = useGetPackages(
    groupName,
    backFilters,
    tableHelpers.sortBy as unknown as ToePackagesSortInput[],
  );

  const onSearch = useCallback(
    (value: string): void => {
      setBackFilters((prev): PackagesVars["filters"] => ({
        ...prev,
        search: value,
      }));
    },
    [setBackFilters],
  );

  const { Filters, options, removeFilter } = usePackagesFilters({
    groupName,
    loading,
    packages,
    setBackFilters,
  });

  const { table } = useTable<IPackage, ToePackagesInput, ToePackagesSort>({
    columns,
    data: packages,
    fetchMore,
    filters: tableHelpers as unknown as TStore<
      ToePackagesInput,
      ToePackagesSort
    >,
    id: "packages-table",
    options: {
      actionsOptions: { onSearch, searchPlaceholder: "Search by dependency" },
      columnOptions: {
        order: [
          "name",
          "rootNickname",
          "version",
          "vulnerable",
          "epss",
          "metadataLatestVersion",
          "metadataLatestVersionCreatedAt",
          "details",
        ],
        pinned: ["name"],
      },
      filtersMode: "server-side",
      loading,
      paginationMode: "server-side",
      rowOptions: {
        expandRender: renderExpandedRow,
        getId: (item): string => `${item.name}-${item.version}`,
      },
    },
    total,
  });

  useEffect((): void => {
    tableHelpers.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backFilters]);

  return (
    <Table table={table}>
      <Table.Options>
        <Table.Controls>
          <Filters />
          <Table.Columns table={table} />
          <AllPackagesTableActions groupName={groupName} />
        </Table.Controls>
      </Table.Options>
      <Table.Caption>
        <AppliedFilters onClose={removeFilter} options={options} />
      </Table.Caption>
      <Table.Content />
      <Table.Footer />
    </Table>
  );
};

export { AllPackagesTab };
